import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import type { HTMLInputTypeAttribute, FC, ChangeEvent } from 'react';
import InputMask from '@components/InputMask';
import ReactDadata from '@components/DaData';
import { creatStandartDadataObj } from '@utils/address';
import API from '@api';
import type { DadataSuggestion } from '@api/dadata';

interface BasicInputProps {
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  className?: string;
  field?: string;
  value?: string;
  onFocus?: () => void;
  onBlur?: (isError?: boolean, isNeedToCheck?: boolean) => void;
  onInputBlur?: (value: string, field?: string) => void;
  onChange?: (value: string, field?: string) => void;
  onSelectDaData?: (
    suggestion: DadataSuggestion,
    coords: number[],
    dadataId: number,
    addDadata: any
  ) => void;
  autocomplete?: string;
  isNewStyleSearch?: boolean;
  skipPhoneValidationOnChange?: boolean;
}

const BasicInput: FC<BasicInputProps> = ({
  placeholder,
  type,
  className,
  field = '',
  value = '',
  onFocus,
  onBlur,
  onInputBlur,
  onChange,
  onSelectDaData: onSelectDaDataProp,
  autocomplete,
  isNewStyleSearch,
  skipPhoneValidationOnChange
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [warningDaData, setWarningDaData] = useState(false);

  // Separate refs for separate concerns
  const prevPropValueRef = useRef(value);
  const prevLocalValueRef = useRef(value);

  // Sync controlled value from parent into local state (layout effect avoids stale render)
  useLayoutEffect(() => {
    if (value !== prevPropValueRef.current) {
      setLocalValue(value || '');
    }
    prevPropValueRef.current = value;
  }, [value]);

  const phoneBlurFn = useCallback(
    (val?: string) => {
      const current = val !== undefined ? val : localValue;
      const isValid =
        (current.includes('+7 (') && current.length === 18) ||
        (!current.includes('+7 (') && current.length === 10);

      if (isValid) {
        setWarningDaData(false);
        onInputBlur?.(current, field);
        onBlur?.(false, true);
      } else {
        setWarningDaData(true);
        onBlur?.(true, true);
      }
    },
    [localValue, field, onInputBlur, onBlur]
  );

  const sendRequestIfHaveTo = useCallback(() => {
    if (field === 'phone') {
      phoneBlurFn();
    } else if (field === 'address') {
      onBlur?.();
    } else {
      onInputBlur?.(localValue, field);
    }
  }, [field, localValue, phoneBlurFn, onBlur, onInputBlur]);

  // Paste detection: if localValue grew by more than 1 char, treat as paste
  useEffect(() => {
    const prev = prevLocalValueRef.current || '';
    if (localValue !== prev && localValue.length > prev.length + 1) {
      sendRequestIfHaveTo();
    }
    prevLocalValueRef.current = localValue;
  }, [localValue, sendRequestIfHaveTo]);

  const changeField = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      if (field === 'Change') {
        if (Number.isInteger(+newValue)) setLocalValue(newValue);
        return;
      }

      setLocalValue(newValue);
      onChange?.(newValue, field);

      if (field === 'phone' && !skipPhoneValidationOnChange) {
        phoneBlurFn(newValue);
      }
    },
    [field, skipPhoneValidationOnChange, phoneBlurFn, onChange]
  );

  const onSelectDaData = useCallback(
    (suggestion: DadataSuggestion) => {
      if (suggestion?.data?.geo_lat && suggestion?.data?.geo_lon) {
        const coords = `${suggestion.data.geo_lon},${suggestion.data.geo_lat}`;
        API.addDadata({ input: creatStandartDadataObj(suggestion.data) })
          .then(res => {
            setWarningDaData(false);
            onSelectDaDataProp?.(
              suggestion,
              [
                Number(suggestion.data.geo_lat),
                Number(suggestion.data.geo_lon)
              ],
              Number(res?.addDadata?.dadataItem?.id),
              res.addDadata
            );
            onBlur?.(false);
          })
          .catch(error => {
            onBlur?.(true, true);
            setWarningDaData(true);
            console.error('addDadata error:', error, coords);
          });
      } else {
        onBlur?.(true, true);
        setWarningDaData(true);
      }
    },
    [onSelectDaDataProp, onBlur]
  );

  const commonProps = {
    className: `${className ?? ''}${warningDaData ? ' warning' : ''}`,
    onFocus
  };

  switch (field) {
    case 'phone':
      return (
        <InputMask
          {...commonProps}
          value={localValue}
          onChange={changeField}
          type='tel'
          placeholder={placeholder}
          name='phone'
          mask='+7 (999) 999-99-99'
          maskChar=''
          onBlur={() => {
            phoneBlurFn();
          }}
        />
      );
    case 'address':
      return (
        <ReactDadata
          {...commonProps}
          name='data[dadata_value]'
          query={localValue}
          placeholder={placeholder}
          onChange={onSelectDaData}
          onBlur={onBlur}
          isNewStyleSearch={isNewStyleSearch}
        />
      );
    case 'comment':
      return (
        <textarea
          {...commonProps}
          value={localValue}
          onChange={changeField}
          placeholder={placeholder}
          spellCheck={true}
          onBlur={() => onInputBlur?.(localValue, field)}
        />
      );
    default:
      return (
        <input
          {...commonProps}
          value={localValue}
          onChange={e => {
            setLocalValue(e.target.value);
            onChange?.(e.target.value, field);
          }}
          type={type}
          placeholder={placeholder}
          onBlur={() => onInputBlur?.(localValue, field)}
          autoComplete={autocomplete ? 'on' : 'off'}
        />
      );
  }
};

export default BasicInput;
